import { CardSection } from "./common/Card";
import { useState } from "react";
import {Alert} from "react-bootstrap";
import Form from "react-bootstrap/Form";
import { DateRangePicker } from "./common/DateRangePicker";

import {ISODateRange} from "../ISODateRange";
import {DownloadCSVButton} from "./common/SubmitButton";
import { Result } from "../client/Result";
import {Select, SelectOption} from "./common/MuiSelect";
import {ReportType} from "../client/DataClient";
import {RadioGroup} from "./common/RadioGroup";
import TimeRangePicker, {TimeRange} from "./common/TimeRangePicker";
import {BooleanSwitch} from "./common/BooleanSwitch";
import {Nullable} from "../utils/UtilityTypes";


interface CapacityMarketDropReportProps {
    download: (range: ISODateRange, reportTYpe: ReportType, bucketId: string, timeRange: TimeRange|undefined) => Promise<Result<void>>
    className?: string
    buckets: string[],
    title?: string,
    label?: string
}

export const CapacityMarketDropReport = ({ download, className, buckets, title = 'Capacity Market Report', label = 'Bucket' }: CapacityMarketDropReportProps) => {
    const byDrop:ReportType = 'byDrop';
    const byBaseline: ReportType = 'byBaseline';

    const [startDate, setStartDate] = useState('')
    const [timeRange, setTimeRange] = useState<Nullable<TimeRange>>({startTime: null, endTime: null})
    const [endDate, setEndDate] = useState('')
    const [bucket, setBucket] = useState('')
    const [reportType, setReportType] = useState<ReportType>(byDrop)
    const [dateRangeValid, setDateRangeValid] = useState(false)
    const [validated, setValidated] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string>()
    const [limitedByTime, setLimitByTime] = useState<boolean>(false)

    async function handleSubmit(event) {

        const form = event.currentTarget
        setErrorMessage(undefined)
        event.preventDefault()
        setValidated(true)

        function isValidTimeRange(range: Nullable<TimeRange>): range is TimeRange {
            return range != null && range.startTime != null && range.endTime != null;
        }

        const isTimeRangeValid = isValidTimeRange(timeRange);
        const isAllowed = !limitedByTime || isTimeRangeValid;

        if (form.checkValidity() && dateRangeValid && isAllowed) {
            // Narrow the type by applying the guard again here
            const timeRangeToUse = limitedByTime && isValidTimeRange(timeRange) ? timeRange : undefined;

            const submissionResult = await download(new ISODateRange(startDate,  endDate), reportType, bucket, timeRangeToUse)
            submissionResult.fold(
                ()  => undefined,
                err => setErrorMessage(`An error occurred - #ask-tech for support. ${err}`)
            )
        } else {
            form.reportValidity()
        }
    }

    return <CardSection className={className} header={title} as="h6">
        <Form noValidate validated={validated} >
            <Select className='mb-3' label={label} data-testid={'bucketId'} initialValue={{ id: bucket, label: bucket}}
                    onChange={(e:SelectOption) => {
                        setErrorMessage(undefined)
                        setBucket(e.id)
                    }}
                    options={buckets.map(b => ({ id: b, label: b}))} />

            <DateRangePicker start={startDate} end={endDate}
                             onEndChange={(end) => setEndDate((end))}
                             onStartChange={(start) => setStartDate(start)}
                             setErrorMessage={error => (setErrorMessage(error))}
                             reportIsValid={valid => setDateRangeValid(valid)} />
            <BooleanSwitch label={"Limit by time"} onChange={() => setLimitByTime(!limitedByTime)} value={limitedByTime} labelPlacement={'end'}/>
            {limitedByTime && <TimeRangePicker
                onTimeRangeChange={(e)=>{setTimeRange(e)}}
                showPresets={true}
                label="Available Hours"
            />}

            <RadioGroup
                    defaultValue={byDrop}
                    values = {[
                        { label: 'By Largest Drop', value: byDrop},
                        { label: 'By Largest Baseline', value: byBaseline}
                    ]}
                    onChange={setReportType}
                />

            <DownloadCSVButton dataTestId={`capacityMarketDropReportButton-${label}`}
                disabled={!(startDate && endDate && (startDate <= endDate) && bucket)}
                onSubmit={handleSubmit}/>
            {errorMessage &&
                <Alert data-testid='errorMessage' variant='danger'>{errorMessage}</Alert>}
        </Form>
    </CardSection>
}
